import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useUserRole } from "@/hooks/useUserRole";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";
import LoadingScreen from "@/components/LoadingScreen";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { formatDistanceToNow, format } from "date-fns";
import { 
  ChevronDown, 
  ChevronRight, 
  MessageSquare, 
  Search, 
  Users,
  ArrowLeft,
  Eye
} from "lucide-react";
import { UserAvatar } from "@/components/UserAvatar";

interface ConversationWithDetails {
  id: string;
  participant1_id: string;
  participant2_id: string;
  updated_at: string;
  last_message: string | null;
  service_id: string | null;
  service_completed: boolean;
  service_started: boolean;
  final_price: number | null;
  payment_completed: boolean;
  participant1?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    avatar_url: string | null;
  } | null;
  participant2?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    avatar_url: string | null;
  } | null;
  service?: {
    title: string;
  } | null;
  messageCount?: number;
}

interface Message {
  id: string;
  text: string;
  sender_id: string;
  created_at: string;
  is_read: boolean;
  file_url?: string | null;
  file_name?: string | null;
}

interface UserGroup {
  userId: string;
  userName: string;
  avatarUrl: string | null;
  conversations: ConversationWithDetails[];
}

const AdminConversations = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isAdmin, loading: roleLoading } = useUserRole();
  const [conversations, setConversations] = useState<ConversationWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());
  const [expandedConversations, setExpandedConversations] = useState<Set<string>>(new Set());
  const [conversationMessages, setConversationMessages] = useState<Record<string, Message[]>>({});
  const [loadingMessages, setLoadingMessages] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (isAdmin) {
      loadConversations();
    }
  }, [isAdmin]);

  const loadConversations = async () => {
    setLoading(true);
    try {
      // Fetch all conversations
      const { data: convos, error } = await supabase
        .from('conversations')
        .select('*')
        .order('updated_at', { ascending: false });

      if (error) throw error;

      // Fetch participant profiles and services
      const enrichedConvos = await Promise.all(
        (convos || []).map(async (convo) => {
          // Fetch profiles
          const [p1Result, p2Result] = await Promise.all([
            supabase.rpc('get_public_profile', { profile_user_id: convo.participant1_id }),
            supabase.rpc('get_public_profile', { profile_user_id: convo.participant2_id })
          ]);

          // Fetch service if exists
          let service = null;
          if (convo.service_id) {
            const { data: serviceData } = await supabase
              .from('services')
              .select('title')
              .eq('id', convo.service_id)
              .single();
            service = serviceData;
          }

          // Count messages
          const { count } = await supabase
            .from('messages')
            .select('id', { count: 'exact', head: true })
            .eq('conversation_id', convo.id);

          return {
            ...convo,
            participant1: p1Result.data?.[0] || null,
            participant2: p2Result.data?.[0] || null,
            service,
            messageCount: count || 0
          };
        })
      );

      setConversations(enrichedConvos);
    } catch (error) {
      console.error("Failed to load conversations:", error);
      toast.error("Failed to load conversations");
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (conversationId: string) => {
    if (conversationMessages[conversationId]) return;
    
    setLoadingMessages(prev => new Set(prev).add(conversationId));
    try {
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      
      setConversationMessages(prev => ({
        ...prev,
        [conversationId]: data || []
      }));
    } catch (error) {
      console.error("Failed to load messages:", error);
      toast.error("Failed to load messages");
    } finally {
      setLoadingMessages(prev => {
        const next = new Set(prev);
        next.delete(conversationId);
        return next;
      });
    }
  };

  const toggleUser = (userId: string) => {
    setExpandedUsers(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const toggleConversation = async (conversationId: string) => {
    const isExpanding = !expandedConversations.has(conversationId);
    
    setExpandedConversations(prev => {
      const next = new Set(prev);
      if (next.has(conversationId)) {
        next.delete(conversationId);
      } else {
        next.add(conversationId);
      }
      return next;
    });

    if (isExpanding) {
      await loadMessages(conversationId);
    }
  };

  const getParticipantName = (participant: ConversationWithDetails['participant1']) => {
    if (!participant) return "Unknown User";
    return `${participant.first_name || ''} ${participant.last_name || ''}`.trim() || "Unknown User";
  };

  const getParticipantInitials = (participant: ConversationWithDetails['participant1']) => {
    if (!participant) return "U";
    return `${participant.first_name?.[0] || ''}${participant.last_name?.[0] || ''}`.toUpperCase() || "U";
  };

  // Group conversations by user
  const groupByUser = (): UserGroup[] => {
    const userMap = new Map<string, UserGroup>();

    conversations.forEach(convo => {
      // Add both participants
      [
        { id: convo.participant1_id, data: convo.participant1 },
        { id: convo.participant2_id, data: convo.participant2 }
      ].forEach(participant => {
        if (!userMap.has(participant.id)) {
          userMap.set(participant.id, {
            userId: participant.id,
            userName: getParticipantName(participant.data),
            avatarUrl: participant.data?.avatar_url || null,
            conversations: []
          });
        }
        
        // Only add if not already in the list for this user
        const group = userMap.get(participant.id)!;
        if (!group.conversations.find(c => c.id === convo.id)) {
          group.conversations.push(convo);
        }
      });
    });

    return Array.from(userMap.values())
      .sort((a, b) => a.userName.localeCompare(b.userName));
  };

  // Filter users based on search
  const filteredUsers = groupByUser().filter(userGroup => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return userGroup.userName.toLowerCase().includes(query) ||
      userGroup.conversations.some(c => 
        c.service?.title?.toLowerCase().includes(query) ||
        c.last_message?.toLowerCase().includes(query)
      );
  });

  if (roleLoading) {
    return <LoadingScreen />;
  }

  if (!user || !isAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin-dashboard')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-foreground">All Conversations</h1>
            <p className="text-muted-foreground">
              View and monitor all conversations between users
            </p>
          </div>
        </div>

        {/* Search */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search users, services, or messages..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-foreground">{conversations.length}</p>
              <p className="text-sm text-muted-foreground">Total Conversations</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-foreground">
                {new Set([...conversations.map(c => c.participant1_id), ...conversations.map(c => c.participant2_id)]).size}
              </p>
              <p className="text-sm text-muted-foreground">Active Users</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-green-600">
                {conversations.filter(c => c.service_completed).length}
              </p>
              <p className="text-sm text-muted-foreground">Completed Services</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-blue-600">
                {conversations.filter(c => c.payment_completed).length}
              </p>
              <p className="text-sm text-muted-foreground">Payments Completed</p>
            </CardContent>
          </Card>
        </div>

        {/* Conversations List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Conversations by User
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <p className="text-muted-foreground">Loading conversations...</p>
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground">No conversations found</p>
              </div>
            ) : (
              <ScrollArea className="h-[60vh]">
                <div className="space-y-2">
                  {filteredUsers.map((userGroup) => (
                    <Collapsible
                      key={userGroup.userId}
                      open={expandedUsers.has(userGroup.userId)}
                      onOpenChange={() => toggleUser(userGroup.userId)}
                    >
                      <CollapsibleTrigger asChild>
                        <div className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors">
                          {expandedUsers.has(userGroup.userId) ? (
                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          )}
                          <UserAvatar
                            userId={userGroup.userId}
                            avatarUrl={userGroup.avatarUrl}
                            fallback={userGroup.userName.split(' ').map(n => n[0]).join('').substring(0, 2)}
                            size="sm"
                            showLevel={false}
                          />
                          <div className="flex-1">
                            <p className="font-medium text-foreground">{userGroup.userName}</p>
                            <p className="text-xs text-muted-foreground">
                              {userGroup.conversations.length} conversation{userGroup.conversations.length !== 1 ? 's' : ''}
                            </p>
                          </div>
                          <Badge variant="secondary">
                            {userGroup.conversations.reduce((acc, c) => acc + (c.messageCount || 0), 0)} messages
                          </Badge>
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="ml-8 pl-4 border-l border-border space-y-2 py-2">
                          {userGroup.conversations.map((convo) => {
                            const otherParticipant = convo.participant1_id === userGroup.userId 
                              ? convo.participant2 
                              : convo.participant1;
                            const otherName = getParticipantName(otherParticipant);
                            
                            return (
                              <Collapsible
                                key={convo.id}
                                open={expandedConversations.has(convo.id)}
                                onOpenChange={() => toggleConversation(convo.id)}
                              >
                                <CollapsibleTrigger asChild>
                                  <div className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/30 cursor-pointer transition-colors">
                                    {expandedConversations.has(convo.id) ? (
                                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                    ) : (
                                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                    )}
                                    <MessageSquare className="h-4 w-4 text-primary" />
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-sm font-medium text-foreground">
                                          with {otherName}
                                        </span>
                                        {convo.service && (
                                          <Badge variant="outline" className="text-xs">
                                            {convo.service.title}
                                          </Badge>
                                        )}
                                        {convo.service_completed && (
                                          <Badge className="bg-green-100 text-green-700 text-xs">
                                            Completed
                                          </Badge>
                                        )}
                                        {convo.payment_completed && (
                                          <Badge className="bg-blue-100 text-blue-700 text-xs">
                                            Paid
                                          </Badge>
                                        )}
                                      </div>
                                      <p className="text-xs text-muted-foreground truncate">
                                        {convo.last_message || 'No messages yet'}
                                      </p>
                                    </div>
                                    <div className="text-right">
                                      <p className="text-xs text-muted-foreground">
                                        {formatDistanceToNow(new Date(convo.updated_at), { addSuffix: true })}
                                      </p>
                                      <p className="text-xs text-muted-foreground">
                                        {convo.messageCount} msgs
                                      </p>
                                    </div>
                                  </div>
                                </CollapsibleTrigger>
                                <CollapsibleContent>
                                  <div className="ml-8 p-3 bg-muted/20 rounded-lg mt-1">
                                    {loadingMessages.has(convo.id) ? (
                                      <p className="text-sm text-muted-foreground text-center py-4">
                                        Loading messages...
                                      </p>
                                    ) : conversationMessages[convo.id]?.length === 0 ? (
                                      <p className="text-sm text-muted-foreground text-center py-4">
                                        No messages in this conversation
                                      </p>
                                    ) : (
                                      <ScrollArea className="max-h-80">
                                        <div className="space-y-3">
                                          {conversationMessages[convo.id]?.map((msg) => {
                                            const sender = msg.sender_id === convo.participant1_id 
                                              ? convo.participant1 
                                              : convo.participant2;
                                            const senderName = getParticipantName(sender);
                                            
                                            return (
                                              <div key={msg.id} className="flex gap-2">
                                                <UserAvatar
                                                  userId={msg.sender_id}
                                                  avatarUrl={sender?.avatar_url}
                                                  fallback={getParticipantInitials(sender)}
                                                  size="sm"
                                                  showLevel={false}
                                                />
                                                <div className="flex-1 min-w-0">
                                                  <div className="flex items-baseline gap-2">
                                                    <span className="text-xs font-medium text-foreground">
                                                      {senderName}
                                                    </span>
                                                    <span className="text-xs text-muted-foreground">
                                                      {format(new Date(msg.created_at), 'MMM d, h:mm a')}
                                                    </span>
                                                  </div>
                                                  <p className="text-sm text-foreground break-words">
                                                    {msg.text.startsWith('[') ? (
                                                      <span className="italic text-muted-foreground">
                                                        {msg.text}
                                                      </span>
                                                    ) : (
                                                      msg.text
                                                    )}
                                                  </p>
                                                  {msg.file_url && (
                                                    <a 
                                                      href={msg.file_url} 
                                                      target="_blank" 
                                                      rel="noopener noreferrer"
                                                      className="text-xs text-primary hover:underline"
                                                    >
                                                      📎 {msg.file_name || 'Attachment'}
                                                    </a>
                                                  )}
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </ScrollArea>
                                    )}
                                    <div className="flex justify-end mt-3 pt-2 border-t border-border">
                                      <Button 
                                        variant="outline" 
                                        size="sm"
                                        onClick={() => navigate(`/admin/conversations/${convo.id}`)}
                                      >
                                        <Eye className="h-4 w-4 mr-1" />
                                        View Full Chat
                                      </Button>
                                    </div>
                                  </div>
                                </CollapsibleContent>
                              </Collapsible>
                            );
                          })}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminConversations;